// solo/RecordSale.jsx — Full till rebuild (Session 31)
// Route: /solo/record-sale          → manual sale entry (from dashboard)
//        /solo/record-sale/:bookingId → appointment checkout (from calendar)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../axiosConfig';
import RetailRecommendations from './RetailRecommendations';
import {
  Box, Paper, Typography, Button, TextField, MenuItem,
  Tabs, Tab, Chip, IconButton, Alert, CircularProgress,
  Divider, Table, TableBody, TableCell, TableHead, TableRow,
  LinearProgress, Tooltip, Grid, Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Add as AddIcon, Delete as DeleteIcon,
  Check as CheckIcon, Block as BlockIcon, Lock as LockIcon,
  TrendingUp as ProfitIcon, Refresh as RefreshIcon, Save as SaveIcon,
} from '@mui/icons-material';

const NAVY = '#2f3542';
const fmt = (n) => parseFloat(n || 0).toFixed(2);
const marginColor = (pct) => pct >= 60 ? 'success' : pct >= 35 ? 'warning' : 'error';

const PrivateBadge = () => (
  <Chip
    icon={<LockIcon sx={{ fontSize: '12px !important' }} />}
    label="Internal only — not shown to client"
    size="small" variant="outlined"
    sx={{ fontSize: '10px', height: 20, borderColor: 'text.disabled', color: 'text.disabled' }}
  />
);

const RecordSale = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  // ── UI state ───────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Appointment ────────────────────────────────────────────
  const [appointment, setAppointment] = useState(null);
  const [clientVisits, setClientVisits] = useState(null);

  // ── Line items ─────────────────────────────────────────────
  const [lineItems, setLineItems] = useState([]);
  const [retailProducts, setRetailProducts] = useState([]);
  const [selectedRetailId, setSelectedRetailId] = useState('');

  // ── Payment ────────────────────────────────────────────────
  const [discount, setDiscount] = useState('0');
  const [tip, setTip] = useState('0');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [splitRows, setSplitRows] = useState([
    { method: 'Cash', amount: '' },
    { method: 'Card', amount: '' },
  ]);

  // ── VAT ────────────────────────────────────────────────────
  const [vatRegistered, setVatRegistered] = useState(false);

  // ── Job Card ───────────────────────────────────────────────
  const [jobCardProducts, setJobCardProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [profit, setProfit] = useState(null);
  const [selectedMaterialProductId, setSelectedMaterialProductId] = useState('');
  const [selectedMaterialQty, setSelectedMaterialQty] = useState('');
  const [jobCardSaving, setJobCardSaving] = useState(false);
  const [jobCardError, setJobCardError] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [jobCardLoading, setJobCardLoading] = useState(false);

  // ── Load appointment ───────────────────────────────────────
  const loadAppointment = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    try {
      const res = await api.get(`/appointments/${bookingId}`);
      const appt = res.data;
      setAppointment(appt);

      const items = [];
      if (appt.is_multi_service && appt.services?.length > 0) {
        appt.services.forEach((svc, i) => {
          items.push({
            id: `svc-${svc.service_id || i}`,
            type: 'service',
            name: svc.service_name,
            duration: svc.duration,
            price: parseFloat(svc.price || 0),
          });
        });
      } else if (appt.service_name) {
        items.push({
          id: 'svc-0',
          type: 'service',
          name: appt.service_name,
          duration: appt.duration,
          price: parseFloat(appt.total_price || appt.price || 0),
        });
      }
      setLineItems(items);

      if (appt.client_id) {
        try {
          const clientRes = await api.get(`/clients/${appt.client_id}`);
          setClientVisits(clientRes.data?.visit_count ?? null);
        } catch { /* not critical */ }
      }
    } catch {
      setError('Could not load appointment. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await api.get('/solo/settings');
      setVatRegistered(res.data?.vat_registered || false);
    } catch { /* defaults to false */ }
  }, []);

  const loadRetailProducts = useCallback(async () => {
    try {
      const res = await api.get('/solo/products');
      const all = res.data.products || [];
      setRetailProducts(all.filter((p) => p.is_retail !== false));
    } catch { /* non-fatal */ }
  }, []);

  const loadJobCardProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const res = await api.get('/solo/products');
      setJobCardProducts(res.data.products || []);
    } catch { /* non-fatal */ }
    finally { setProductsLoading(false); }
  }, []);

  const loadMaterials = useCallback(async () => {
    if (!bookingId) return;
    setJobCardLoading(true);
    try {
      const res = await api.get(`/solo/appointments/${bookingId}/materials`);
      const existing = res.data.materials || [];
      if (existing.length > 0) {
        setMaterials(existing);
      } else {
        try {
          const prefill = await api.get(`/solo/appointments/${bookingId}/materials/prefill`);
          setMaterials(prefill.data.materials || []);
        } catch { setMaterials([]); }
      }
    } catch (err) {
      if (err.response?.status !== 404) setJobCardError('Could not load job card data.');
    } finally {
      setJobCardLoading(false);
    }
  }, [bookingId]);

  const loadProfit = useCallback(async () => {
    if (!bookingId) return;
    try {
      const res = await api.get(`/solo/appointments/${bookingId}/profit`);
      setProfit(res.data);
    } catch { /* optional */ }
  }, [bookingId]);

  useEffect(() => {
    loadSettings();
    loadRetailProducts();
    loadJobCardProducts();
    if (bookingId) {
      loadAppointment();
      loadMaterials();
      loadProfit();
    }
  }, [bookingId, loadAppointment, loadSettings, loadRetailProducts, loadJobCardProducts, loadMaterials, loadProfit]);

  // ── Service IDs for retail recommendations ─────────────────
  // TOP LEVEL — never inside a callback or other function
  const serviceIds = useMemo(() => {
    if (!appointment) return [];
    if (appointment.is_multi_service && appointment.services?.length > 0) {
      return appointment.services.map(s => s.service_id).filter(Boolean);
    }
    return appointment.service_id ? [appointment.service_id] : [];
  }, [appointment]);

  // ── Calculations ───────────────────────────────────────────
  const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
  const depositPaid = parseFloat(appointment?.deposit_amount || 0);
  const discountAmt = parseFloat(discount) || 0;
  const tipAmt = parseFloat(tip) || 0;
  const dueToday = Math.max(0, subtotal - depositPaid - discountAmt + tipAmt);
  const vatAmount = vatRegistered ? dueToday / 6 : 0;
  const splitTotal = splitRows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const splitRemaining = Math.max(0, dueToday - splitTotal);

  // ── Line item actions ──────────────────────────────────────
  const updatePrice = (id, value) =>
    setLineItems((prev) => prev.map((item) => (item.id === id ? { ...item, price: value } : item)));

  const removeItem = (id) =>
    setLineItems((prev) => prev.filter((item) => item.id !== id));

  const addRetailProduct = () => {
    const product = retailProducts.find((p) => p.id === parseInt(selectedRetailId));
    if (!product) return;
    setLineItems((prev) => [...prev, {
      id: `retail-${product.id}-${Date.now()}`,
      type: 'retail',
      name: product.name,
      price: parseFloat(product.retail_price || product.price || 0),
    }]);
    setSelectedRetailId('');
  };

  // ── Split payment actions ──────────────────────────────────
  const updateSplitRow = (idx, field, value) =>
    setSplitRows((prev) => prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  const addSplitRow = () => setSplitRows((prev) => [...prev, { method: 'Cash', amount: '' }]);
  const removeSplitRow = (idx) => setSplitRows((prev) => prev.filter((_, i) => i !== idx));

  // ── Complete sale ──────────────────────────────────────────
  const handleCompleteSale = async () => {
    if (lineItems.length === 0 && !bookingId) {
      setError('Add at least one service or product before recording the sale.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        payment_type: paymentMethod,
        discount: discountAmt,
        tip: tipAmt,
        line_items: lineItems.map((item) => ({
          name: item.name, type: item.type, price: parseFloat(item.price),
        })),
        split_payments: paymentMethod === 'Split' ? splitRows : null,
        vat_registered: vatRegistered,
      };

      if (bookingId) {
        await api.post(`/appointments/${bookingId}/checkout`, payload);
      } else {
        await api.post('/solo/sales', {
          amount: dueToday,
          description: lineItems.map((i) => i.name).join(', ') || 'Manual sale',
          payment_type: paymentMethod,
          date: new Date().toISOString().split('T')[0],
        });
      }

      setSnackbar({ open: true, message: 'Sale recorded successfully!', severity: 'success' });
      setTimeout(() => navigate(bookingId ? '/solo/bookings' : '/solo-dashboard'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record sale. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── No show ────────────────────────────────────────────────
  const handleNoShow = async () => {
    if (!bookingId) return;
    setSaving(true);
    try {
      await api.post(`/appointments/${bookingId}/cancel`, { status: 'no_show' });
      setSnackbar({ open: true, message: 'Marked as no-show.', severity: 'info' });
      setTimeout(() => navigate('/solo/bookings'), 1500);
    } catch {
      setError('Failed to mark as no-show.');
    } finally {
      setSaving(false);
    }
  };

  // ── Job card ───────────────────────────────────────────────
  const handleAddMaterial = () => {
    if (!selectedMaterialProductId) { setJobCardError('Please select a product.'); return; }
    const qty = parseFloat(selectedMaterialQty);
    if (!qty || qty <= 0) { setJobCardError('Enter a quantity greater than 0.'); return; }
    const product = jobCardProducts.find((p) => p.id === parseInt(selectedMaterialProductId));
    if (!product) return;
    if (materials.find((m) => m.product_id === product.id)) {
      setJobCardError(`${product.name} is already on this job card.`); return;
    }
    setMaterials((prev) => [...prev, {
      product_id: product.id,
      product_name: product.name,
      quantity_used: qty,
      cost_per_unit_at_time: product.cost_per_gram || 0,
      line_cost: parseFloat(((product.cost_per_gram || 0) * qty).toFixed(2)),
    }]);
    setSelectedMaterialProductId('');
    setSelectedMaterialQty('');
    setJobCardError(null);
  };

  const handleMaterialQtyChange = (productId, newQty) => {
    setMaterials((prev) => prev.map((m) => {
      if (m.product_id !== productId) return m;
      const qty = parseFloat(newQty) || 0;
      return { ...m, quantity_used: qty, line_cost: parseFloat((m.cost_per_unit_at_time * qty).toFixed(2)) };
    }));
  };

  const handleRemoveMaterial = (productId) =>
    setMaterials((prev) => prev.filter((m) => m.product_id !== productId));

  const handleSaveJobCard = async () => {
    if (!bookingId) return;
    if (materials.length === 0) { setJobCardError('Add at least one material before saving.'); return; }
    setJobCardSaving(true);
    setJobCardError(null);
    try {
      await api.post(`/solo/appointments/${bookingId}/materials`, {
        materials: materials.map((m) => ({ product_id: m.product_id, quantity_used: m.quantity_used })),
      });
      await loadProfit();
      setSnackbar({ open: true, message: 'Job card saved. Stock updated.', severity: 'success' });
    } catch (err) {
      setJobCardError(err.response?.data?.error || 'Could not save job card. Please try again.');
    } finally {
      setJobCardSaving(false);
    }
  };

  const totalMaterialsCost = materials.reduce((sum, m) => sum + (m.line_cost || 0), 0);
  const selectedMaterialProduct = jobCardProducts.find((p) => p.id === parseInt(selectedMaterialProductId));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 1.5, md: 3 } }}>

      <Button startIcon={<ArrowBackIcon />}
        onClick={() => navigate(bookingId ? '/solo/bookings' : '/solo-dashboard')}
        sx={{ mb: 2 }}>
        {bookingId ? 'Back to Appointments' : 'Back to Dashboard'}
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Checkout" />
        {bookingId && <Tab label="Job Card" />}
      </Tabs>

      {/* ══════════════════ TAB 0: CHECKOUT ══════════════════ */}
      {activeTab === 0 && (
        <Grid container spacing={2} alignItems="flex-start">

          {/* ── LEFT PANEL ── */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 2.5 }}>

              {appointment && (
                <Box sx={{ mb: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" fontWeight={600}>{appointment.client_name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {appointment.client_phone}
                    {clientVisits != null && ` · ${clientVisits} visits`}
                  </Typography>
                </Box>
              )}

              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                Services &amp; Products
              </Typography>

              <Box sx={{ mt: 1, mb: 2 }}>
                {lineItems.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No services loaded. Add a retail product or record manually.
                  </Typography>
                )}
                {lineItems.map((item) => (
                  <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
                          {item.name}
                        </Typography>
                        <Chip label={item.type} size="small"
                          color={item.type === 'service' ? 'primary' : 'secondary'}
                          sx={{ height: 18, fontSize: '10px' }} />
                      </Box>
                      {item.duration && (
                        <Typography variant="caption" color="text.secondary">{item.duration} min</Typography>
                      )}
                    </Box>
                    <TextField
                      value={item.price} onChange={(e) => updatePrice(item.id, e.target.value)}
                      size="small" type="number" variant="standard"
                      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', width: 68, fontSize: 14 } }}
                      InputProps={{ startAdornment: <Typography variant="body2" sx={{ mr: 0.25, color: 'text.secondary' }}>£</Typography> }}
                    />
                    <IconButton size="small" onClick={() => removeItem(item.id)}
                      sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>

              {/* Retail dropdown */}
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <TextField select value={selectedRetailId}
                  onChange={(e) => setSelectedRetailId(e.target.value)}
                  size="small" label="Add retail product..." sx={{ flex: 1 }}>
                  <MenuItem value=""><em>Select product</em></MenuItem>
                  {retailProducts.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name} — £{fmt(p.retail_price || p.price)}
                    </MenuItem>
                  ))}
                </TextField>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={addRetailProduct}
                  disabled={!selectedRetailId} size="small" sx={{ whiteSpace: 'nowrap' }}>
                  + Add
                </Button>
              </Box>

              {/* Retail recommendations — uses serviceIds memo from component level */}
              <RetailRecommendations
                serviceIds={serviceIds}
                onAddToSale={(rec) => {
                  setLineItems(prev => [...prev, {
                    id: `retail-${rec.product_id}-${Date.now()}`,
                    type: 'retail',
                    name: rec.product_name,
                    price: parseFloat(rec.retail_price || 0),
                  }]);
                }}
              />

              <Divider sx={{ mt: 2, mb: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                  <Typography variant="body2">£{fmt(subtotal)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Discount (£)</Typography>
                  <TextField value={discount} onChange={(e) => setDiscount(e.target.value)}
                    size="small" type="number" variant="standard"
                    inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', width: 80, fontSize: 14 } }} />
                </Box>
                {depositPaid > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>✓ Deposit paid</Typography>
                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>−£{fmt(depositPaid)}</Typography>
                  </Box>
                )}
                {vatRegistered && dueToday > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', pt: 0.5 }}>
                    Includes £{fmt(vatAmount)} VAT collected for HMRC
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* ── RIGHT PANEL ── */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>Payment</Typography>

              <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
                {['Cash', 'Card', 'Split'].map((method) => (
                  <Button key={method}
                    variant={paymentMethod === method ? 'contained' : 'outlined'}
                    onClick={() => setPaymentMethod(method)} size="small"
                    sx={{ flex: 1, ...(paymentMethod === method && { bgcolor: NAVY, borderColor: NAVY, '&:hover': { bgcolor: '#1e2530', borderColor: '#1e2530' } }) }}>
                    {method}
                  </Button>
                ))}
              </Box>

              {paymentMethod === 'Split' && (
                <Box sx={{ mb: 2 }}>
                  {splitRows.map((row, idx) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                      <TextField select value={row.method}
                        onChange={(e) => updateSplitRow(idx, 'method', e.target.value)}
                        size="small" sx={{ flex: 1 }}>
                        {['Cash', 'Card', 'Bank Transfer', 'Voucher'].map((m) => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </TextField>
                      <TextField value={row.amount}
                        onChange={(e) => updateSplitRow(idx, 'amount', e.target.value)}
                        size="small" type="number" label="£"
                        inputProps={{ min: 0, step: 0.01 }} sx={{ width: 88 }} />
                      {splitRows.length > 1 && (
                        <IconButton size="small" onClick={() => removeSplitRow(idx)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  <Button size="small" startIcon={<AddIcon />} onClick={addSplitRow} sx={{ mb: 1 }}>Add row</Button>
                  {splitRemaining > 0.01 && (
                    <Alert severity="warning" sx={{ py: 0.5, fontSize: '12px' }}>
                      £{fmt(splitRemaining)} still unallocated
                    </Alert>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Tip (£)</Typography>
                <TextField value={tip} onChange={(e) => setTip(e.target.value)}
                  size="small" type="number" variant="standard"
                  inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', width: 80, fontSize: 14 } }} />
              </Box>

              <Box sx={{ bgcolor: NAVY, color: 'white', borderRadius: 1.5, px: 2.5, py: 1.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)' }}>Total due</Typography>
                <Typography variant="h4" fontWeight={700}>£{fmt(dueToday)}</Typography>
              </Box>

              <Button fullWidth variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
                onClick={handleCompleteSale} disabled={saving}
                sx={{ mb: 1.5, py: 1.5, bgcolor: NAVY, '&:hover': { bgcolor: '#1e2530' }, fontSize: '15px' }}>
                Complete &amp; record sale
              </Button>

              {bookingId && (
                <Button fullWidth variant="outlined" color="inherit"
                  startIcon={<BlockIcon />} onClick={handleNoShow} disabled={saving}
                  sx={{ color: 'text.secondary', borderColor: 'divider' }}>
                  Mark as no-show
                </Button>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ══════════════════ TAB 1: JOB CARD ══════════════════ */}
      {activeTab === 1 && bookingId && (
        <Paper sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PrivateBadge />
          </Box>

          {jobCardError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJobCardError(null)}>{jobCardError}</Alert>
          )}

          {jobCardLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading job card...</Typography>
            </Box>
          ) : (
            <>
              <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1 }}>Materials used</Typography>

              {materials.some((m) => m.prefilled) && (
                <Alert severity="info" sx={{ mb: 1.5, fontSize: '12px', py: 0.5 }}>
                  Pre-filled from your calculator. Adjust quantities if needed then save.
                </Alert>
              )}

              {materials.length > 0 ? (
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      {['Product', 'Used (g)', 'Cost/g', 'Line cost', ''].map((h, i) => (
                        <TableCell key={h} align={i >= 1 && i <= 3 ? 'right' : 'left'}
                          sx={{ fontWeight: 500, fontSize: '12px', color: 'text.secondary' }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {materials.map((m) => (
                      <TableRow key={m.product_id}>
                        <TableCell sx={{ fontSize: '13px' }}>{m.product_name}</TableCell>
                        <TableCell align="right">
                          <TextField type="number" value={m.quantity_used}
                            onChange={(e) => handleMaterialQtyChange(m.product_id, e.target.value)}
                            size="small" variant="standard"
                            inputProps={{ min: 0.1, step: 0.5, style: { textAlign: 'right', width: 64, fontSize: 13 } }} />
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                          £{parseFloat(m.cost_per_unit_at_time || 0).toFixed(4)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 500 }}>
                          £{fmt(m.line_cost)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0 }}>
                          <IconButton size="small" onClick={() => handleRemoveMaterial(m.product_id)} color="error">
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontWeight: 500, fontSize: '13px', borderTop: '1px solid', borderColor: 'divider' }}>
                        Total materials cost
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500, fontSize: '13px', borderTop: '1px solid', borderColor: 'divider' }}>
                        £{fmt(totalMaterialsCost)}
                      </TableCell>
                      <TableCell sx={{ borderTop: '1px solid', borderColor: 'divider' }} />
                    </TableRow>
                  </TableBody>
                </Table>
              ) : (
                <Alert severity="info" sx={{ mb: 2, fontSize: '13px' }}>
                  No materials logged yet. Add products used below.
                </Alert>
              )}

              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, borderStyle: 'dashed', mb: 3 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Add a product</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <TextField select label="Product" value={selectedMaterialProductId}
                    onChange={(e) => { setSelectedMaterialProductId(e.target.value); setJobCardError(null); }}
                    size="small" sx={{ flex: 2, minWidth: 180 }} disabled={productsLoading}>
                    {productsLoading ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : jobCardProducts.length === 0 ? (
                      <MenuItem disabled>No products in stock list</MenuItem>
                    ) : jobCardProducts.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}{p.cost_per_gram ? ` — £${parseFloat(p.cost_per_gram).toFixed(4)}/g` : ' — no cost set'}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField label="Grams used" type="number" value={selectedMaterialQty}
                    onChange={(e) => setSelectedMaterialQty(e.target.value)}
                    size="small" sx={{ width: 110 }} inputProps={{ min: 0.1, step: 0.5 }}
                    helperText={selectedMaterialProduct?.default_usage_grams ? `Typical: ${selectedMaterialProduct.default_usage_grams}g` : null} />
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddMaterial} sx={{ height: 40, whiteSpace: 'nowrap' }}>
                    Add
                  </Button>
                </Box>
              </Paper>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <ProfitIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="subtitle2" fontWeight={500}>Profit summary</Typography>
                <PrivateBadge />
                {profit && (
                  <Tooltip title="Refresh profit calculation">
                    <IconButton size="small" onClick={loadProfit}>
                      <RefreshIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {profit ? (
                <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', mb: 3 }}>
                  <Table size="small">
                    <TableBody>
                      {[
                        { label: 'Service charge', value: `£${fmt(profit.service_charge)}`, color: null },
                        { label: 'Labour (est.)', value: `−£${fmt(profit.labour_cost)}`, color: 'error.main' },
                        { label: `Materials${materials.length === 0 ? ' (none logged)' : ''}`, value: `−£${fmt(profit.materials_cost)}`, color: 'error.main' },
                        { label: 'Overhead (est.)', value: `−£${fmt(profit.overhead_cost)}`, color: 'error.main' },
                      ].map((row) => (
                        <TableRow key={row.label}>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '13px' }}>{row.label}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', color: row.color || 'inherit' }}>{row.value}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 500, fontSize: '13px' }}>Gross profit</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, fontSize: '13px' }}>£{fmt(profit.gross_profit)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Margin</Typography>
                      <Chip label={`${parseFloat(profit.margin_pct || 0).toFixed(1)}%`} size="small"
                        color={marginColor(profit.margin_pct)} sx={{ height: 20, fontSize: '11px' }} />
                    </Box>
                    <LinearProgress variant="determinate" value={Math.min(100, profit.margin_pct || 0)}
                      color={marginColor(profit.margin_pct)} sx={{ height: 6, borderRadius: 3 }} />
                    {profit.note && (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75 }}>
                        {profit.note}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ) : (
                <Alert severity="info" sx={{ fontSize: '13px', mb: 3 }}>Save materials to see the profit breakdown.</Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button variant="contained"
                  startIcon={jobCardSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveJobCard} disabled={jobCardSaving || materials.length === 0}
                  sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#1e2530' } }}>
                  {jobCardSaving ? 'Saving...' : 'Save job card'}
                </Button>
              </Box>
            </>
          )}
        </Paper>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RecordSale;